/**
 * ScenarioControls Component
 *
 * Input controls for scenario modeling parameters
 */

import { Box, TextField, Slider, Typography, Paper } from '@mui/material';
import { ScenarioInputs } from '../types/scenario.types';

interface ScenarioControlsProps {
  inputs: ScenarioInputs;
  onChange: (inputs: ScenarioInputs) => void;
  availableRoles: string[];
}

export default function ScenarioControls({
  inputs,
  onChange,
  availableRoles,
}: ScenarioControlsProps) {
  const handleUtilizationChange = (_event: Event, value: number | number[]) => {
    onChange({ ...inputs, targetUtilization: (value as number) / 100 });
  };

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...inputs, monthlyPatientVolume: Number(event.target.value) });
  };

  const handleGrowthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...inputs, growthRatePercent: Number(event.target.value) });
  };

  const handleVisitsPerPatientChange = (role: string, value: number) => {
    onChange({
      ...inputs,
      visitsPerPatient: {
        ...inputs.visitsPerPatient,
        [role]: value,
      },
    });
  };

  const handlePlanningMonthsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...inputs, planningMonths: Number(event.target.value) });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Scenario Parameters
      </Typography>

      {/* Target Utilization Slider */}
      <Box sx={{ mb: 3 }}>
        <Typography gutterBottom>
          Target Utilization: {Math.round(inputs.targetUtilization * 100)}%
        </Typography>
        <Slider
          value={inputs.targetUtilization * 100}
          onChange={handleUtilizationChange}
          min={50}
          max={95}
          step={5}
          marks={[
            { value: 50, label: '50%' },
            { value: 70, label: '70%' },
            { value: 80, label: '80%' },
            { value: 90, label: '90%' },
          ]}
          valueLabelDisplay="auto"
        />
        <Typography variant="caption" color="text.secondary">
          Higher utilization = more efficient but less flexibility
        </Typography>
      </Box>

      {/* Monthly Patient Volume */}
      <TextField
        label="Monthly Patient Volume"
        type="number"
        size="small"
        fullWidth
        value={inputs.monthlyPatientVolume}
        onChange={handleVolumeChange}
        inputProps={{ min: 1, max: 10000, step: 10 }}
        sx={{ mb: 2 }}
      />

      {/* Visits Per Patient by Role */}
      <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
        Visits Per Patient:
      </Typography>
      {availableRoles.map((role) => (
        <TextField
          key={role}
          label={`${role} Visits/Patient`}
          type="number"
          size="small"
          fullWidth
          value={inputs.visitsPerPatient[role] || 0}
          onChange={(e) => handleVisitsPerPatientChange(role, Number(e.target.value))}
          inputProps={{ min: 0, max: 50, step: 1 }}
          sx={{ mb: 1 }}
        />
      ))}

      {/* Growth Rate */}
      <TextField
        label="Growth Rate (%)"
        type="number"
        size="small"
        fullWidth
        value={inputs.growthRatePercent}
        onChange={handleGrowthChange}
        inputProps={{ min: -50, max: 100, step: 5 }}
        sx={{ mb: 2, mt: 2 }}
        helperText="Expected month-over-month growth"
      />

      {/* Planning Horizon */}
      <TextField
        label="Planning Months"
        type="number"
        size="small"
        fullWidth
        value={inputs.planningMonths}
        onChange={handlePlanningMonthsChange}
        inputProps={{ min: 1, max: 24, step: 1 }}
        helperText="How many months to project"
      />
    </Paper>
  );
}
